import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaBatteryFull, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const CapacitorsInParallelCalculator = () => {
  const [voltage, setVoltage] = useState('');
  const [capacitor1, setCapacitor1] = useState('');
  const [capacitor2, setCapacitor2] = useState('');
  const [capacitor3, setCapacitor3] = useState('');
  const [totalCapacitance, setTotalCapacitance] = useState('');
  const [totalCharge, setTotalCharge] = useState('');
  const [charge1, setCharge1] = useState('');
  const [charge2, setCharge2] = useState('');
  const [charge3, setCharge3] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateParallel = () => {
    if (voltage && capacitor1 && capacitor2 && capacitor3) {
      const V = parseFloat(voltage);
      const C1 = parseFloat(capacitor1);
      const C2 = parseFloat(capacitor2);
      const C3 = parseFloat(capacitor3);

      // Calculate total capacitance
      const C_total = C1 + C2 + C3;
      setTotalCapacitance(C_total.toFixed(2));

      // Calculate charge for each capacitor using Q = C * V
      const Q1 = C1 * V;
      const Q2 = C2 * V;
      const Q3 = C3 * V;
      const Q_total = Q1 + Q2 + Q3;

      setCharge1(Q1.toFixed(2));
      setCharge2(Q2.toFixed(2));
      setCharge3(Q3.toFixed(2));
      setTotalCharge(Q_total.toFixed(2));
      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!voltage || !capacitor1 || !capacitor2 || !capacitor3 || !totalCapacitance || !totalCharge) return null;

    const V = parseFloat(voltage);
    const C1 = parseFloat(capacitor1);
    const C2 = parseFloat(capacitor2);
    const C3 = parseFloat(capacitor3);
    const Q1 = parseFloat(charge1);
    const Q2 = parseFloat(charge2);
    const Q3 = parseFloat(charge3);

    // Generate data for charge distribution
    const series = [
      {
        name: 'Charge Distribution',
        data: [
          { x: 'C1', y: Q1 },
          { x: 'C2', y: Q2 },
          { x: 'C3', y: Q3 }
        ]
      }
    ];

    const options = {
      chart: {
        type: 'bar',
        height: 400,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true,
          columnWidth: '40%'
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function(val) {
          return val.toFixed(2) + 'C';
        }
      },
      xaxis: {
        title: {
          text: 'Charge (C)',
        },
        labels: {
          formatter: function(val) {
            return val.toFixed(2);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Capacitor',
        }
      },
      title: {
        text: 'Charge Distribution Across Parallel Capacitors',
        align: 'center',
      },
      fill: {
        colors: ['#4F46E5']
      },
      tooltip: {
        y: {
          formatter: function(val) {
            return val.toFixed(2) + 'C';
          }
        }
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-50 to-pink-50">
      <Helmet>
        <title>Capacitors in Parallel Calculator | Physics Tool</title>
        <meta name="description" content="Calculate total capacitance, charge distribution, and energy storage for capacitors in parallel. Interactive calculator with bar chart visualization." />
        <meta name="keywords" content="capacitors in parallel calculator, physics calculator, electronics calculator, electrical calculator" />
        <meta property="og:title" content="Capacitors in Parallel Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate total capacitance, charge distribution, and energy storage for capacitors in parallel. Interactive calculator with bar chart visualization." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Capacitors in Parallel Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate total capacitance, charge distribution, and energy storage for capacitors in parallel. Interactive calculator with bar chart visualization." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/capacitors-in-parallel" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-purple-600 mb-4">
            <FaBatteryFull />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Capacitors in Parallel Calculator</h1>
          <p className="text-xl text-gray-600">Calculate total capacitance and charge distribution</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Supply Voltage (V)</label>
                <input
                  type="number"
                  value={voltage}
                  onChange={(e) => setVoltage(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter supply voltage (V)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Capacitor 1 (F)</label>
                <input
                  type="number"
                  value={capacitor1}
                  onChange={(e) => setCapacitor1(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter capacitor 1 value (F)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Capacitor 2 (F)</label>
                <input
                  type="number"
                  value={capacitor2}
                  onChange={(e) => setCapacitor2(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter capacitor 2 value (F)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Capacitor 3 (F)</label>
                <input
                  type="number"
                  value={capacitor3}
                  onChange={(e) => setCapacitor3(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter capacitor 3 value (F)"
                />
              </div>
              <div>
                <button
                  onClick={calculateParallel}
                  className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate
                </button>
              </div>

              {voltage && capacitor1 && capacitor2 && capacitor3 && totalCapacitance && totalCharge && (
                <div className="mt-4 p-4 bg-purple-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-purple-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-purple-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-purple-800">Supply Voltage = {voltage} V</p>
                        <p className="text-purple-800">Total Capacitance = {totalCapacitance} F</p>
                        <p className="text-purple-800">Total Charge = {totalCharge} C</p>
                        <p className="text-purple-800">Charge on C1 = {charge1} C</p>
                        <p className="text-purple-800">Charge on C2 = {charge2} C</p>
                        <p className="text-purple-800">Charge on C3 = {charge3} C</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the formulas:
                      <br />
                      1. Total Capacitance = C1 + C2 + C3
                      <br />
                      2. Charge = Capacitance × Voltage
                      <br />
                      <br />
                      Where:
                      <br />
                      V = Supply Voltage = {voltage} V
                      <br />
                      C1 = Capacitor 1 = {capacitor1} F
                      <br />
                      C2 = Capacitor 2 = {capacitor2} F
                      <br />
                      C3 = Capacitor 3 = {capacitor3} F
                      <br />
                      C_total = Total Capacitance = {totalCapacitance} F
                      <br />
                      Q_total = Total Charge = {totalCharge} C
                      <br />
                      Q1 = Charge on C1 = {charge1} C
                      <br />
                      Q2 = Charge on C2 = {charge2} C
                      <br />
                      Q3 = Charge on C3 = {charge3} C
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Charge Distribution</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="bar"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows the charge distribution across each capacitor in parallel
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the supply voltage (V)</li>
            <li>Enter the values of three capacitors in farads (F)</li>
            <li>Click "Calculate" to get the results</li>
            <li>View the bar chart showing charge distribution</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Parallel Capacitance Calculation</h3>
              <p>Calculate total capacitance for parallel circuit</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Charge Distribution</h3>
              <p>Calculate and visualize charge on each capacitor</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Energy Storage</h3>
              <p>Calculate total energy storage capability</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for electronics and physics students</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Parallel Connection?</h3>
              <p className="text-gray-600">
                In a parallel connection, capacitors are connected across the same voltage source, each storing charge independently.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Voltage should be in volts (V)</li>
                  <li>Capacitance should be in farads (F)</li>
                  <li>The result will be in coulombs (C) for charge</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the bar chart work?</h3>
              <p className="text-gray-600">
                The bar chart shows:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Charge distribution across each capacitor</li>
                  <li>Each bar represents one capacitor's charge</li>
                  <li>Helps visualize how charge accumulates in a parallel circuit</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Parallel capacitors are used in many applications:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Power supply filtering</li>
                  <li>Energy storage systems</li>
                  <li>Decoupling circuits</li>
                  <li>High-capacity storage solutions</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Electronics engineers design circuits</li>
                  <li>Students understand parallel circuits</li>
                  <li>Teachers demonstrate electrical concepts</li>
                  <li>Anyone working with capacitors in parallel</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapacitorsInParallelCalculator;
